import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, LoadingBlock, StatusChip } from "@/components/Common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { fmtDate, fmtTime } from "@/lib/utils-app";
import { History, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const ACTION_LABEL = {
    "room.update": "Room update",
    "reservation.check_in": "Check-in",
    "reservation.check_out": "Check-out",
    "booking.create": "Booking created",
    "task.update": "Housekeeping update",
    "housekeeping.optimize": "Housekeeping optimized",
    "ticket.create": "Ticket created",
    "ticket.update": "Ticket update",
    "ticket.delete": "Ticket deleted",
    "review.suggest_reply": "Review reply drafted",
    "review.reply": "Review reply sent",
    "integration.toggle": "Integration toggled",
    "migration.import": "Migration import",
    "backup.export": "Backup exported",
    "backup.restore": "Backup restored",
};

export default function AuditLog() {
    const [items, setItems] = useState(null);
    const [summary, setSummary] = useState(null);
    const [type, setType] = useState("all");
    const [q, setQ] = useState("");

    const load = async () => {
        const params = {};
        if (type !== "all") params.target_type = type;
        const [a, s] = await Promise.all([
            api.get("/audit/events", { params }),
            api.get("/audit/summary"),
        ]);
        setItems(a.data);
        setSummary(s.data);
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [type]);

    const filtered = !items
        ? null
        : q
          ? items.filter((e) =>
                JSON.stringify(e).toLowerCase().includes(q.toLowerCase()),
            )
          : items;

    return (
        <>
            <PageHeader
                eyebrow="Audit trail"
                title="Audit Log"
                subtitle="Every critical change is captured here. Filter by target type or search."
                actions={
                    <Button variant="outline" size="sm" onClick={load} data-testid="audit-refresh">
                        <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                }
            />

            {summary && (
                <Card className="mb-4">
                    <CardContent className="py-4">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-sm">
                                <span className="text-muted-foreground">Total events: </span>
                                <span className="font-semibold tabular">{summary.total}</span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {(summary.by_action || []).slice(0, 12).map((s, i) => (
                                <span key={i} className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-xs">
                                    <span className="text-muted-foreground">{ACTION_LABEL[s.action] || s.action}</span>
                                    <span className="tabular font-semibold">{s.count}</span>
                                </span>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <History className="w-4 h-4" /> Events
                    </CardTitle>
                    <div className="flex gap-2">
                        <Input
                            placeholder="Search…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="w-48"
                            data-testid="audit-search"
                        />
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger className="w-48" data-testid="audit-target-filter">
                                <SelectValue placeholder="All targets" />
                            </SelectTrigger>
                            <SelectContent>
                                {[
                                    "all",
                                    "reservation",
                                    "room",
                                    "housekeeping_task",
                                    "maintenance_ticket",
                                    "review",
                                    "integration",
                                    "backup",
                                    "housekeeping",
                                ].map((t) => (
                                    <SelectItem key={t} value={t} className="capitalize">
                                        {t.replace("_", " ")}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardHeader>
                <CardContent>
                    {!filtered ? (
                        <LoadingBlock rows={5} />
                    ) : filtered.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">
                            No events yet for these filters. Make a change in any module to see entries here.
                        </div>
                    ) : (
                        <div className="divide-y divide-border" data-testid="audit-list">
                            {filtered.map((e) => (
                                <div key={e.id} className="py-3 flex items-start gap-3" data-testid="audit-row">
                                    <div className="text-xs tabular text-muted-foreground min-w-[120px]">
                                        {fmtDate(e.ts)} · {fmtTime(e.ts)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium">{ACTION_LABEL[e.action] || e.action}</div>
                                        <div className="text-xs text-muted-foreground truncate">
                                            {e.actor} · {e.target_type}
                                            {e.target_id ? ` · ${e.target_id.slice(0, 8)}` : ""}
                                        </div>
                                        {e.metadata && Object.keys(e.metadata).length > 0 && (
                                            <div className="text-xs text-muted-foreground mt-0.5 truncate">
                                                {Object.entries(e.metadata).slice(0, 3).map(([k, v]) => (
                                                    <span key={k} className="mr-2">
                                                        <span className="text-muted-foreground/70">{k}:</span>{" "}
                                                        <span>{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <StatusChip status={"info"} label={e.target_type} />
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </>
    );
}
